import Button from '@rio-cloud/rio-uikit/Button';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import EventUtils from '@rio-cloud/rio-uikit/EventUtils';
import HereMap from '@rio-cloud/rio-uikit/Map';
import Marker from '@rio-cloud/rio-uikit/Marker';
import type { MapApi, Position } from '@rio-cloud/rio-uikit/mapTypes';
import Route from '@rio-cloud/rio-uikit/Route';
import SingleMapMarker from '@rio-cloud/rio-uikit/SingleMapMarker';
import { type ReactNode, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import type { ChargingStop } from '#domain/ChargingStop.ts';
import type { TourStop } from '#domain/TourStop.ts';
import { EV_DEMO_PROFILE } from '#features/tourCreate/service/smartRoute.ts';
import {
    getTourStopMapBounds,
    getTourStopMapItems,
    getTourStopMapSegments,
} from '#features/tourCreate/utils/tourStopsMap.utils.ts';

import { config } from '../../../config.ts';
import { TourStopMarkers, type TourStopMarkersHandle } from './TourStopMarkers.tsx';
import { TourStopsEmptyState } from './TourStopsEmptyState.tsx';

type TourStopsMapProps = {
    stops: TourStop[];
    height?: number;
    routePositions?: Position[];
    chargingStops?: ChargingStop[];
    batteryCapacity?: number;
};

const ROUTE_STYLE = { color: '#0784e3', width: 4, borderWidth: 0 };

const RouteMap = ({
    model,
    height,
}: {
    model: {
        items: ReturnType<typeof getTourStopMapItems>;
        segments: ReturnType<typeof getTourStopMapSegments>;
        bounds: ReturnType<typeof getTourStopMapBounds>;
        routePositions?: Position[];
        chargingStops?: ChargingStop[];
        batteryCapacity: number;
    };
    height: number;
}) => {
    const intl = useIntl();
    const markersRef = useRef<TourStopMarkersHandle>(null);
    const eventListenerMap = useMemo(
        () => ({
            [EventUtils.MAP_VIEW_CHANGE_START]: () => markersRef.current?.resetForMapMovement(),
            [EventUtils.MAP_VIEW_CHANGE_END]: () => markersRef.current?.recalculate(),
        }),
        []
    );
    return (
        <HereMap
            credentials={{ apikey: config.hereApiKey }}
            boundingBox={model.bounds}
            height={height}
            showCluster={false}
            eventListenerMap={eventListenerMap}
        >
            {(api: MapApi) => (
                <>
                    {model.routePositions ? (
                        <Route positions={model.routePositions} hasArrows style={ROUTE_STYLE} />
                    ) : (
                        model.segments.map(segment => (
                            <Route
                                key={segment.id}
                                positions={[segment.from, segment.to]}
                                hasArrows
                                style={ROUTE_STYLE}
                            />
                        ))
                    )}
                    <TourStopMarkers ref={markersRef} api={api} items={model.items} />
                    {model.chargingStops?.map((stop, index) => (
                        <Marker
                            key={stop.id}
                            position={stop.position}
                            icon={
                                <div
                                    role='img'
                                    title={stop.name}
                                    aria-label={intl.formatMessage(
                                        { id: 'intl-msg:ordercom-cloud-frontend.smart-route.ev-stop' },
                                        {
                                            number: index + 1,
                                            minutes: Math.ceil(stop.duration / 60),
                                            arrival: Math.round((stop.arrivalCharge / model.batteryCapacity) * 100),
                                            departure: Math.round((stop.targetCharge / model.batteryCapacity) * 100),
                                        }
                                    )}
                                >
                                    <SingleMapMarker
                                        iconNames={['filling-e-station']}
                                        markerColor='bg-map-marker-success'
                                        clickable={false}
                                        name={`${intl.formatMessage({ id: 'intl-msg:ordercom-cloud-frontend.routing-settings.charging' })} · ${stop.name || index + 1}`}
                                    />
                                </div>
                            }
                        />
                    ))}
                </>
            )}
        </HereMap>
    );
};

const PreviewMessage = ({ children }: { children: ReactNode }) => (
    <div className='flex-1-1 display-grid place-items-center min-height-250 bg-lightest rounded text-color-dark'>
        <span className='padding-20 text-center'>{children}</span>
    </div>
);

export const TourStopsMap = ({
    stops,
    height = 400,
    routePositions,
    chargingStops,
    batteryCapacity = EV_DEMO_PROFILE.maxCharge,
}: TourStopsMapProps) => {
    const intl = useIntl();
    const [expanded, setExpanded] = useState(false);
    const mapModel = useMemo(() => {
        const items = getTourStopMapItems(stops);
        return {
            items,
            segments: getTourStopMapSegments(items),
            bounds: getTourStopMapBounds([...items, ...(routePositions ?? []).map(position => ({ position }))]),
            routePositions,
            batteryCapacity,
            chargingStops,
        };
    }, [stops, routePositions, chargingStops, batteryCapacity]);
    const missingStopCount = stops.length - mapModel.items.length;
    const title = intl.formatMessage({
        id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.route-preview.title',
    });

    return (
        <section className='flex-shrink-0 display-flex flex-column gap-10' aria-label={title}>
            <div className='display-flex flex-wrap align-items-center justify-content-between gap-10'>
                <h4 className='margin-0'>{title}</h4>
                {mapModel.items.length > 0 && config.hereApiKey && (
                    <Button bsStyle='muted' bsSize='sm' iconName='fullscreen' onClick={() => setExpanded(true)}>
                        {intl.formatMessage({
                            id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.route-preview.expand',
                        })}
                    </Button>
                )}
            </div>
            {stops.length === 0 ? (
                <TourStopsEmptyState outerClassName='border-none flex-1-1 margin-0 bg-lightest rounded' />
            ) : mapModel.items.length === 0 ? (
                <PreviewMessage>
                    {intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.route-preview.no-coordinates',
                    })}
                </PreviewMessage>
            ) : !config.hereApiKey ? (
                <PreviewMessage>
                    {intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.route-preview.unavailable',
                    })}
                </PreviewMessage>
            ) : (
                <>
                    {missingStopCount > 0 && (
                        <output className='alert alert-warning margin-0 display-block'>
                            {intl.formatMessage(
                                {
                                    id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.route-preview.omitted',
                                },
                                { count: missingStopCount }
                            )}
                        </output>
                    )}
                    <div className='position-relative overflow-hidden border rounded shadow-subtle min-height-250'>
                        <RouteMap model={mapModel} height={height} />
                    </div>
                </>
            )}
            {expanded && mapModel.items.length > 0 && config.hereApiKey && (
                <Dialog
                    show
                    title={title}
                    bsSize='lg'
                    onClose={() => setExpanded(false)}
                    body={<RouteMap model={mapModel} height={Math.max(250, Math.min(750, window.innerHeight - 200))} />}
                />
            )}
        </section>
    );
};
